import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------
// DESIGN-ONLY STAGE — the "Edit" dialog, opened only for Mpesa receipts
// (the row's Edit button is disabled for Cash — money handed over in
// cash doesn't need re-splitting the way a traceable Mpesa payment
// does). A member's single Mpesa payment might cover more than this one
// period — e.g. they send 5,000, only 3,000 is meant for this period and
// the other 2,000 should count toward a later one. This lets you split
// the receipt's total across this period and/or other periods for the
// same group before saving.
//
// To wire it up later:
//   1. Replace `buildGroupPeriods` with a real fetch of this group's
//      other periods (mirrors PerformancePeriods.js's pattern).
//   2. `handleSave` is a stub — this is where the reallocation actually
//      gets persisted (presumably splitting/duplicating the underlying
//      Mpesa transaction across periods server-side).
// ---------------------------------------------------------------------

const formatKSh = (amount) => `KSh ${Math.round(amount || 0).toLocaleString()}`;

const formatShortDate = (date) => {
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ---- Mock data — remove once buildGroupPeriods calls the real API ----
// A handful of this same group's periods around the current one, so
// there's something to pick from besides "this period".
const buildGroupPeriods = (period) => {
  if (!period) return [];
  const base = new Date(period.meetingDate);
  const offsets = [-60, -30, 0, 30, 60];
  return offsets.map((offsetDays) => {
    const d = new Date(base);
    d.setDate(d.getDate() + offsetDays);
    return {
      id: offsetDays === 0 ? period.id : `${period.group.id}-offset-${offsetDays}`,
      meetingDate: d,
      isCurrent: offsetDays === 0,
    };
  });
};
// ---- end mock data ----

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}`;

const DistributeMpesaDialog = ({ open, onClose, period, receipt }) => {
  const groupPeriods = useMemo(() => buildGroupPeriods(period), [period]);
  const [allocations, setAllocations] = useState([]);

  // Reset to "everything on this period" whenever a different receipt is
  // opened.
  useEffect(() => {
    if (!open || !receipt || !period) return;
    setAllocations([{ id: nextRowId(), periodId: period.id, amount: receipt.amount }]);
  }, [open, receipt?.id, period?.id]);

  const totalAllocated = useMemo(
    () => allocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0),
    [allocations]
  );
  const remaining = receipt ? Math.round((receipt.amount - totalAllocated) * 100) / 100 : 0;

  const usedPeriodIds = allocations.map(a => a.periodId).filter(Boolean);
  const hasDuplicates = new Set(usedPeriodIds).size !== usedPeriodIds.length;
  const allRowsComplete = allocations.every(a => a.periodId && parseFloat(a.amount) > 0);
  const canSave = allRowsComplete && remaining === 0 && !hasDuplicates;

  const handleAddRow = () => {
    setAllocations(prev => [...prev, { id: nextRowId(), periodId: '', amount: remaining > 0 ? remaining : 0 }]);
  };

  const handleRemoveRow = (id) => {
    setAllocations(prev => (prev.length > 1 ? prev.filter(a => a.id !== id) : prev));
  };

  const handlePeriodChange = (id, periodId) => {
    setAllocations(prev => prev.map(a => (a.id === id ? { ...a, periodId } : a)));
  };

  const handleAmountChange = (id, value) => {
    setAllocations(prev => prev.map(a => (a.id === id ? { ...a, amount: value } : a)));
  };

  const handleSave = () => {
    alert('Saving this distribution is not wired up yet — this is where the split gets persisted once the backend supports it.');
    onClose();
  };

  if (!open || !period || !receipt) return null;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative z-10 rounded-lg shadow-xl max-w-lg w-full bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Distribute Mpesa Payment</h3>
            <p className="text-sm text-gray-500 mt-0.5">{receipt.memberName} &middot; {receipt.memberPhone}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 mb-4">
            <span className="text-sm text-gray-600">Total received (Mpesa)</span>
            <span className="text-lg font-bold text-gray-900">{formatKSh(receipt.amount)}</span>
          </div>

          <p className="text-sm font-medium text-gray-700 mb-2">Allocate to periods</p>

          <div className="space-y-2">
            {allocations.map((row) => (
              <div key={row.id} className="flex items-center gap-2">
                <select
                  value={row.periodId}
                  onChange={(e) => handlePeriodChange(row.id, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select period...</option>
                  {groupPeriods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {formatShortDate(p.meetingDate)}{p.isCurrent ? ' (this period)' : ''}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  value={row.amount}
                  onChange={(e) => handleAmountChange(row.id, e.target.value)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => handleRemoveRow(row.id)}
                  disabled={allocations.length === 1}
                  className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={handleAddRow}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Allocation
          </button>

          <div className={`mt-4 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
            remaining === 0
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {remaining === 0 ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            {remaining === 0
              ? 'Fully allocated'
              : remaining > 0
                ? `${formatKSh(remaining)} still unallocated`
                : `${formatKSh(Math.abs(remaining))} over-allocated`}
          </div>
          {hasDuplicates && (
            <p className="mt-2 text-sm text-red-600">The same period is selected more than once — combine those rows.</p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              canSave ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistributeMpesaDialog;